/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useEffect, useContext, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import { DebounceInput } from "react-debounce-input";
import { LayoutContext } from "../../components/layout";
import { addTimeToDateString } from "../../utils/inject";
import {
  mapFilterKeys,
  mapFilterKeysForPatientReport,
  mapKeyFilters,
  mapKeyFiltersForPatientReport,
  mapKeyFiltersForInsurrance,
} from "../../constants/PatientListFilterConstants";
import { getKeyFromValue } from "../../utils/structUtils";
import { GET_PATIENTS_API_V2_ENABLED } from "../../constants/AppConfigConstants";

const AllReportsFilter = ({ onFilterChange, filter, activeTab }) => {
  const { user, appConfigStructure, readPhysicians } = useContext(LayoutContext);
  const [physicians, setPhysicians] = useState([]);
  const defaultFilter = useRef(null);
  const filterReference = useRef(null);

  const tab = localStorage.getItem("selectedTab");

  switch (tab) {
    case "insurance":
      defaultFilter.current = mapFilterKeys;
      filterReference.current = mapKeyFiltersForInsurrance;
      break;
    case "pending":
      defaultFilter.current = mapFilterKeys;
      filterReference.current = mapKeyFilters;
      break;
    case "patient-merge":
      defaultFilter.current = mapFilterKeys;
      filterReference.current = mapKeyFilters;
      break;
    default:
      if (appConfigStructure[GET_PATIENTS_API_V2_ENABLED]) {
        defaultFilter.current = mapFilterKeysForPatientReport;
        filterReference.current = mapKeyFiltersForPatientReport;
      } else {
        defaultFilter.current = mapFilterKeys;
        filterReference.current = mapKeyFilters;
      }
      break;
  }
  useEffect(() => {
    if (readPhysicians) setPhysicians(readPhysicians);
  }, [readPhysicians]);

  const onChange = ({ target }) => {
    const alias = target.dataset.key;
    const tfilter = { ...filter };
    const fkey = defaultFilter.current[alias];
    const { key, com } = filterReference.current[fkey];

    if (target.value) {
      tfilter[fkey] = { key, com, value: target.value };
    } else if (tfilter[fkey]) {
      delete tfilter[fkey];
    }
    onFilterChange({ ...tfilter });
  };

  const getValue = (alias) => {
    const fkey = defaultFilter.current[alias];
    const result = (filter && filter[fkey]?.value) || "";

    return result;
  };

  return (
    <form className="search toggleSearch" id="searchContent">
      <div className="advOpt">
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Last Name</span>
              <DebounceInput
                id="lastname-search-text-box"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Last Name..."
                value={getValue(getKeyFromValue(defaultFilter.current, "lname"))}
                onChange={onChange}
                data-key={getKeyFromValue(defaultFilter.current, "lname")}
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>First Name</span>
              <DebounceInput
                id="first-name-search-text-box"
                debounceTimeout={800}
                type="text"
                placeholder="Search by First Name..."
                value={getValue(getKeyFromValue(defaultFilter.current, "fname"))}
                onChange={onChange}
                data-key={getKeyFromValue(defaultFilter.current, "fname")}
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Date of Birth</span>
            </label>
            <DatePicker
              placeholder="Search by Date of Birth..."
              id="date-of-birth-search-text-box"
              selected={
                filter &&
                filter[defaultFilter.current["dob"]]?.value &&
                new Date(addTimeToDateString(getValue("dob")))
              }
              onChange={(date) => {
                const tfilter = { ...filter };
                tfilter[defaultFilter.current["dob"]] = {
                  com: "$eq",
                  key: "dob",
                  value: date.toStandardDate(),
                };
                onFilterChange({ ...tfilter });
              }}
            />
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Location</span>
              <select
                id="location-search-text-box"
                onChange={onChange}
                value={getValue(getKeyFromValue(defaultFilter.current, "location"))}
                data-key={getKeyFromValue(defaultFilter.current, "location")}
              >
                <option value="">Search by Location...</option>
                {user.locations.map((location) => (
                  <option key={location.name} value={location.name}>
                    {location.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Medical Record Number</span>
              <DebounceInput
                id="mrn-search-text-box"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Medical Record Number..."
                value={getValue(getKeyFromValue(defaultFilter.current, "patientLocation.mrn"))}
                onChange={onChange}
                data-key={getKeyFromValue(defaultFilter.current, "patientLocation.mrn")}
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Model</span>
              <DebounceInput
                id="device-model-search-text-box"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Device Model..."
                value={getValue(getKeyFromValue(defaultFilter.current, "model"))}
                onChange={onChange}
                data-key={getKeyFromValue(defaultFilter.current, "model")}
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Serial Number</span>
              <DebounceInput
                id="device-serial-number-search-text-box"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Device Serial Number..."
                value={getValue(getKeyFromValue(defaultFilter.current, "serial"))}
                onChange={onChange}
                data-key={getKeyFromValue(defaultFilter.current, "serial")}
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Type</span>
              <select
                id="device-type-search-text-box"
                onChange={onChange}
                value={getValue(getKeyFromValue(defaultFilter.current, "type"))}
                data-key={getKeyFromValue(defaultFilter.current, "type")}
              >
                <option value="">Search by Device Type...</option>
                {[
                  "PACEMAKER",
                  "CRTP",
                  "ICD",
                  "CRTD",
                  "ILR",
                  "IPG",
                  "PPM",
                  "MONITOR",
                  "ICM",
                  "MCT",
                  "EVENT",
                  "HOLTER",
                  "BGM",
                  "BP_CUFF",
                  "WEIGHTSCALE",
                  "OTHER",
                ].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Manufacturer</span>
              <select
                id="device-manufacturer-search-text-box"
                onChange={onChange}
                value={getValue(getKeyFromValue(defaultFilter.current, "manufacturer"))}
                data-key={getKeyFromValue(defaultFilter.current, "manufacturer")}
              >
                <option value="">Search by Device Manufacturer...</option>
                {["BIOTRONIK", "BOSTONSCI", "MEDTRONIC", "STJUDE", "BIOTEL", "OTHER"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Prior Authorization</span>
              <select
                id="prior-authorization-text-box"
                onChange={onChange}
                value={getValue(getKeyFromValue(defaultFilter.current, "authorize"))}
                data-key={getKeyFromValue(defaultFilter.current, "authorize")}
              >
                <option value="">Search by Authorization...</option>
                <option value={true}>YES</option>
                <option value={false}>NO</option>
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Service Status</span>
              <select
                id="service-status-text-box"
                onChange={onChange}
                value={getValue("serviceStatus")}
                data-key="serviceStatus"
              >
                <option value="">Select Status...</option>
                <option value="PENDING">Pending</option>
                <option value="COMPLETE">Complete</option>
                <option value="POC_ONLY">POC Only</option>
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Patient Status</span>
              <select
                id="patient-status-text-box"
                onChange={onChange}
                value={getValue(getKeyFromValue(defaultFilter.current, "patientStatus"))}
                data-key={getKeyFromValue(defaultFilter.current, "patientStatus")}
              >
                <option value="">Select Status...</option>
                <option value="ACTIVE">Active</option>
                <option value="DECEASED">Deceased</option>
                <option value="REMOVED">Removed</option>
              </select>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Following Physician</span>
              <select
                id="following-physician-text-box"
                onChange={onChange}
                value={getValue(getKeyFromValue(defaultFilter.current, "followingPhysician"))}
                data-key={getKeyFromValue(defaultFilter.current, "followingPhysician")}
              >
                <option value="">Search by Following Physician...</option>
                {physicians
                  .filter((i) => i.rtDefibrillators === true || i.rtPaceMakers === true)
                  .sort((a, b) => {
                    if (a.family > b.family) return 1;
                    if (b.family > a.family) return -1;
                    return 0;
                  })
                  .map((item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >{`${item.family}, ${item.name} (${item.username})`}</option>
                  ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Sub-location</span>
              <select
                id="sub-location-text-box"
                onChange={onChange}
                value={getValue(getKeyFromValue(defaultFilter.current, "subLocation"))}
                data-key={getKeyFromValue(defaultFilter.current, "subLocation")}
              >
                <option value="">Search by Sub-location...</option>
                {user.locations[0].subLocations &&
                  JSON.parse(user.locations[0].subLocations).map((subLocation) => (
                    <option key={subLocation} value={subLocation}>
                      {subLocation}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25" />
          <div className="col col-m-50 col25" />
        </div>
        {activeTab === "insurance" ? (
          <>
            <div className="row rowStretch">
              <div className="col col-m-50 col25">
                <label className="inCal">
                  <span>Insurance Name</span>
                  <DebounceInput
                    debounceTimeout={800}
                    type="text"
                    placeholder="Search by Insurance Name"
                    value={getValue("insuranceName")}
                    onChange={onChange}
                    data-key="insuranceName"
                  />
                </label>
              </div>
              <div className="col col-m-50 col25">
                <label className="inCal">
                  <span>Insurance Status</span>
                  <select onChange={onChange} value={getValue("insuranceStatus")} data-key="insuranceStatus">
                    <option value="">Search by Insurance Status...</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
              </div>
              <div className="col col-m-50 col25">
                <label className="inCal">
                  <span>Insurance Type</span>
                  <select onChange={onChange} value={getValue("insuranceType")} data-key="insuranceType">
                    <option value="">Search by Insurance Type...</option>
                    {["PRIVATE", "MEDICARE", "HMO", "PPO", "FFS", "CAPITATED", "SELF-PLAY", "OTHER"].map(
                      (opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>
              <div className="col col-m-50 col25">
                <label className="inCal">
                  <span>Update Count</span>
                  <DebounceInput
                    debounceTimeout={800}
                    type="text"
                    placeholder="Search by Update Count..."
                    value={getValue("updateCount")}
                    onChange={onChange}
                    data-key="updateCount"
                  />
                </label>
              </div>
            </div>
            <div className="row rowStretch">
              <div className="col col-m-50 col25">
                <label className="inCal">
                  <span>Update Date</span>
                </label>
                <DatePicker
                  placeholder="Search by Update Date..."
                  selected={
                    filter &&
                    filter[defaultFilter.current["updateDate"]]?.value &&
                    new Date(addTimeToDateString(getValue("updateDate")))
                  }
                  onChange={(date) => {
                    const tfilter = { ...filter };
                    tfilter[defaultFilter.current["updateDate"]] = {
                      com: "$gte",
                      key: "updateDate",
                      value: date.toStandardDate(),
                    };
                    onFilterChange({ ...tfilter });
                  }}
                />
              </div>
              <div className="col col-m-50 col25"></div>
              <div className="col col-m-50 col25"></div>
              <div className="col col-m-50 col25"></div>
            </div>
          </>
        ) : null}
      </div>
    </form>
  );
};

export default AllReportsFilter;
